import { makeStyles } from '@material-ui/core/styles'
import {
  CALLOUT_BACKGROUND,
  CALLOUT_FOREGROUND,
  OUTLINED_INPUT_BORDER,
} from 'lib/form-field-styles'

export const SEGMENTED_BUTTON_BORDER_RADIUS = 24
export const SEGMENTED_BUTTON_BORDER = OUTLINED_INPUT_BORDER
export const SEGMENTED_BUTTON_PADDING_Y = 10
export const SEGMENTED_BUTTON_PADDING_X = 24
export const SEGMENTED_BUTTON_CHECK_SIZE = 16
export const SEGMENTED_BUTTON_CHECK_GAP = 6

const segmentedButtonBaseStyles = {
  color: CALLOUT_FOREGROUND,
  backgroundColor: '#FFFFFF',
  textTransform: 'none',
  fontWeight: 400,
  fontSize: '0.875rem',
  lineHeight: '1.25rem',
  letterSpacing: 'normal',
}

const segmentedButtonSelectedStyles = {
  backgroundColor: CALLOUT_BACKGROUND,
  color: CALLOUT_FOREGROUND,
  '&:hover': {
    backgroundColor: CALLOUT_BACKGROUND,
  },
}

const segmentedGroupOutlineStyles = {
  borderRadius: SEGMENTED_BUTTON_BORDER_RADIUS,
  overflow: 'hidden',
  border: `1px solid ${SEGMENTED_BUTTON_BORDER}`,
  backgroundColor: '#FFFFFF',
  display: 'inline-flex',
  alignItems: 'stretch',
  width: 'auto',
}

const segmentedButtonCornerRadiusStyles = {
  borderRadius: 0,
  '&:first-of-type': {
    borderTopLeftRadius: SEGMENTED_BUTTON_BORDER_RADIUS,
    borderBottomLeftRadius: SEGMENTED_BUTTON_BORDER_RADIUS,
    borderTopRightRadius: 0,
    borderBottomRightRadius: 0,
  },
  '&:last-of-type': {
    borderTopRightRadius: SEGMENTED_BUTTON_BORDER_RADIUS,
    borderBottomRightRadius: SEGMENTED_BUTTON_BORDER_RADIUS,
    borderTopLeftRadius: 0,
    borderBottomLeftRadius: 0,
  },
  '&:only-of-type': {
    borderRadius: SEGMENTED_BUTTON_BORDER_RADIUS,
  },
}

const segmentedToggleButtonStyles = {
  ...segmentedButtonBaseStyles,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  border: 'none',
  margin: 0,
  padding: `${SEGMENTED_BUTTON_PADDING_Y}px ${SEGMENTED_BUTTON_PADDING_X}px`,
  minWidth: 96,
  flex: '1 1 0',
  alignSelf: 'stretch',
  whiteSpace: 'nowrap',
  ...segmentedButtonCornerRadiusStyles,
  '& .MuiToggleButton-label': {
    whiteSpace: 'nowrap',
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
  },
  '&:not(:first-child)': {
    marginLeft: 0,
    borderLeft: `1px solid ${SEGMENTED_BUTTON_BORDER}`,
  },
  '&$selected + &': {
    borderLeft: `1px solid ${SEGMENTED_BUTTON_BORDER}`,
    marginLeft: 0,
  },
  '&:hover': {
    backgroundColor: '#FFFFFF',
  },
  '&$selected': {
    ...segmentedButtonSelectedStyles,
  },
  '&$selected&.MuiToggleButton-primary': {
    ...segmentedButtonSelectedStyles,
  },
  '&$sizeSmall': {
    padding: `${SEGMENTED_BUTTON_PADDING_Y}px ${SEGMENTED_BUTTON_PADDING_X}px`,
  },
}

export const useSegmentedToggleButtonStyles = makeStyles({
  root: segmentedToggleButtonStyles,
})

export const segmentedButtonThemeOverrides = {
  MuiToggleButtonGroup: {
    root: {
      ...segmentedGroupOutlineStyles,
    },
    groupedHorizontal: {
      '&:not(:first-child)': {
        marginLeft: 0,
        borderLeft: `1px solid ${SEGMENTED_BUTTON_BORDER}`,
        borderTopLeftRadius: '0 !important',
        borderBottomLeftRadius: '0 !important',
      },
      '&:not(:last-child)': {
        borderTopRightRadius: '0 !important',
        borderBottomRightRadius: '0 !important',
      },
      '&:first-child': {
        borderTopLeftRadius: SEGMENTED_BUTTON_BORDER_RADIUS,
        borderBottomLeftRadius: SEGMENTED_BUTTON_BORDER_RADIUS,
      },
      '&:last-child': {
        borderTopRightRadius: SEGMENTED_BUTTON_BORDER_RADIUS,
        borderBottomRightRadius: SEGMENTED_BUTTON_BORDER_RADIUS,
      },
    },
  },
  MuiToggleButton: {
    root: segmentedToggleButtonStyles,
  },
}

export const segmentedToggleButtonGroupSx = {
  ...segmentedGroupOutlineStyles,
  '& .MuiToggleButtonGroup-grouped': {
    ...segmentedButtonBaseStyles,
    border: 'none !important',
    margin: '0 !important',
    padding: `${SEGMENTED_BUTTON_PADDING_Y}px ${SEGMENTED_BUTTON_PADDING_X}px`,
    minWidth: 96,
    flex: '1 1 0',
    alignSelf: 'stretch',
    whiteSpace: 'nowrap',
    justifyContent: 'center',
    borderRadius: '0 !important',
    '& .MuiToggleButton-label': {
      whiteSpace: 'nowrap',
      width: '100%',
      display: 'flex',
      justifyContent: 'center',
    },
    '&:first-of-type': {
      borderTopLeftRadius: `${SEGMENTED_BUTTON_BORDER_RADIUS}px !important`,
      borderBottomLeftRadius: `${SEGMENTED_BUTTON_BORDER_RADIUS}px !important`,
      borderTopRightRadius: '0 !important',
      borderBottomRightRadius: '0 !important',
    },
    '&:last-of-type': {
      borderTopRightRadius: `${SEGMENTED_BUTTON_BORDER_RADIUS}px !important`,
      borderBottomRightRadius: `${SEGMENTED_BUTTON_BORDER_RADIUS}px !important`,
      borderTopLeftRadius: '0 !important',
      borderBottomLeftRadius: '0 !important',
    },
    '&:not(:first-of-type)': {
      marginLeft: '0 !important',
      borderLeft: `1px solid ${SEGMENTED_BUTTON_BORDER} !important`,
    },
    '&.Mui-selected': {
      ...segmentedButtonSelectedStyles,
    },
    '&.Mui-selected.MuiToggleButton-primary': {
      ...segmentedButtonSelectedStyles,
    },
    '&:hover': {
      backgroundColor: '#FFFFFF',
    },
  },
}

export const segmentedToggleChipGroupSx = {
  flexWrap: 'wrap',
  gap: 1,
  width: '100%',
  border: 'none',
  backgroundColor: 'transparent',
  overflow: 'visible',
  '& .MuiToggleButtonGroup-grouped': {
    ...segmentedButtonBaseStyles,
    textTransform: 'none',
    border: `1px solid ${SEGMENTED_BUTTON_BORDER} !important`,
    borderRadius: `${SEGMENTED_BUTTON_BORDER_RADIUS}px !important`,
    margin: 0,
    padding: '8px 16px',
    flex: '0 0 auto',
    '&.Mui-selected': {
      backgroundColor: CALLOUT_BACKGROUND,
      color: CALLOUT_FOREGROUND,
      '&:hover': {
        backgroundColor: CALLOUT_BACKGROUND,
      },
    },
    '&:hover': {
      backgroundColor: '#FFFFFF',
    },
  },
}
